/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import Autosuggest from 'react-autosuggest';
import type {
  ChangeEvent,
  GetSuggestionValue,
  OnSuggestionSelected,
  OnSuggestionsClearRequested,
  RenderSuggestion,
  RenderSuggestionsContainer,
  SuggestionsFetchRequested,
} from 'react-autosuggest';

type Option = { label: string; value: string };

type Props = {
  options: Array<Option>;
  value: string;
  onChange?: (suggestion: Option) => void;
};

function Autocomplete({ options, onChange, value }: Props) {
  const [suggestions, setSuggestions] = React.useState<Array<Option>>([]);
  const [inputValue, setInputValue] = React.useState<string>('');

  const styles = {
    root: css`
      position: relative;
    `,

    container: css`
      border: 1px solid var(--cds-color-grey-200);
      min-width: 400px;
      background-color: var(--cds-color-neutral-background-primary);
      position: absolute;
    `,

    suggestion: css`
      min-height: 32px;
      display: flex;
      align-items: center;

      &:hover {
        background-color: var(--cds-color-grey-50);
      }
    `,
  };

  React.useEffect(() => {
    if (value) {
      const option = options.find((o) => o.value === value);
      if (option) {
        setInputValue(option.label);
      }
    }
  }, [value, options]);

  const handleSuggestionsFetchRequested: SuggestionsFetchRequested = ({ value }) => {
    if (!value) {
      setSuggestions([]);
      return;
    }

    const v = value.trim().toLowerCase();

    if (v.length === 0) {
      setSuggestions([]);
    } else {
      setSuggestions(options.filter((o) => o.label.toLowerCase().includes(v)));
    }
  };

  const handleSuggestionsClearRequested: OnSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const handleSuggestionSelected: OnSuggestionSelected<Option> = (event, { suggestion }) => {
    onChange?.(suggestion);
  };

  const getSuggestionValue: GetSuggestionValue<Option> = (suggestion: Option) => suggestion.label;

  const renderSuggestion: RenderSuggestion<Option> = (suggestion: Option) => (
    <div css={styles.suggestion}>{suggestion.label}</div>
  );

  const renderSuggestionsContainer: RenderSuggestionsContainer = ({ containerProps, children }) => {
    return (
      <div {...containerProps} css={styles.container}>
        {children}
      </div>
    );
  };

  const handleChange = (event: React.FormEvent<HTMLInputElement>, { newValue }: ChangeEvent) => {
    setInputValue(newValue);
  };

  return (
    <div css={styles.root}>
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
        onSuggestionsClearRequested={handleSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={handleSuggestionSelected}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        inputProps={{ value: inputValue, onChange: handleChange, autoComplete: 'off' }}
      />
    </div>
  );
}

export default Autocomplete;
